// Overage evidence timeline (BP8 verb #5) — the customer-facing audit artifact.
//
// Read-only render of GET /api/v1/licenses/:id/overage-evidence (BP7): per-metric
// usage-vs-pool high-water timeline + the billed (term-peak) overage. Exportable
// as CSV or JSON (one-click), since this is the receipt a customer is shown for a
// true-up charge. Never recomputes — it shows the recorded samples verbatim.
import React, { useEffect, useState } from 'react';
import { apiRequest } from '@/lib/supabase';
import { FileDown, ShieldCheck } from 'lucide-react';

interface EvidenceSample {
  sample_at: string;
  usage: number;
  pool_at_sample: number;
  overage: number;
  source: string;
}
interface MetricEvidence {
  metric: string;
  timeline: EvidenceSample[];
  billed_overage: number;
  peak_at: string | null;
  period: { from: string | null; to: string | null };
}
interface Evidence {
  license_id: string;
  contract_id: string | null;
  metrics: MetricEvidence[];
}

const METRIC_LABEL: Record<string, string> = {
  vendors: 'Vendors',
  orgs: 'Orgs',
  ciic_customers: 'CIIC Customers',
};

function download(filename: string, content: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function OverageEvidence({ licenseId, licenseKey }: { licenseId: string; licenseKey?: string }) {
  const [evidence, setEvidence] = useState<Evidence | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const e = await apiRequest<Evidence>(`/licenses/${licenseId}/overage-evidence`);
        setEvidence(e);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load evidence');
      } finally {
        setLoading(false);
      }
    })();
  }, [licenseId]);

  // Never trust the payload shape: an error/partial object could arrive without a
  // well-formed metrics[] (or a metric without a timeline[]) and crash .some/.map.
  const metrics = Array.isArray(evidence?.metrics) ? evidence!.metrics : [];
  const hasData = metrics.some((m) => (m.timeline?.length ?? 0) > 0);

  const exportCsv = () => {
    if (!evidence) return;
    const lines = ['metric,sample_at,usage,pool_at_sample,overage,source'];
    for (const m of metrics) {
      for (const s of m.timeline || []) {
        lines.push([m.metric, s.sample_at, s.usage, s.pool_at_sample, s.overage, s.source].join(','));
      }
    }
    download(`overage-evidence-${licenseKey || licenseId}.csv`, lines.join('\n'), 'text/csv');
  };
  const exportJson = () => {
    if (!evidence) return;
    download(`overage-evidence-${licenseKey || licenseId}.json`, JSON.stringify(evidence, null, 2), 'application/json');
  };

  const btn: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8,
    border: '1px solid var(--border)', background: 'var(--surface)', cursor: 'pointer', fontSize: 12.5, fontWeight: 600,
  };

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ShieldCheck size={15} /> Overage Evidence
        </span>
        {hasData && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={exportCsv} style={btn}><FileDown size={13} /> CSV</button>
            <button onClick={exportJson} style={btn}><FileDown size={13} /> JSON</button>
          </div>
        )}
      </div>
      <div className="card-body">
        {loading ? (
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading evidence…</div>
        ) : error ? (
          <div style={{ fontSize: 13, color: '#DC2626' }}>{error}</div>
        ) : !hasData ? (
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            No overage recorded. Usage has stayed within pool — nothing to bill.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {metrics
              .filter((m) => (m.timeline?.length ?? 0) > 0)
              .map((m) => (
                <div key={m.metric}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 700 }}>{METRIC_LABEL[m.metric] || m.metric}</span>
                    <span style={{ fontSize: 12, color: m.billed_overage > 0 ? '#D97706' : 'var(--text-muted)', fontWeight: 600 }}>
                      Billed peak: {m.billed_overage.toLocaleString()} {m.peak_at ? `(${new Date(m.peak_at).toLocaleDateString()})` : ''}
                    </span>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid var(--border)' }}>
                          {['When', 'Usage', 'Pool', 'Overage', 'Source'].map((h) => (
                            <th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-muted)' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(m.timeline || []).map((s, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid var(--border)', background: s.overage > 0 ? '#FFFBF5' : 'transparent' }}>
                            <td style={{ padding: '6px 10px', fontSize: 12, color: 'var(--text-secondary)' }}>{new Date(s.sample_at).toLocaleString()}</td>
                            <td style={{ padding: '6px 10px', fontSize: 12.5, fontWeight: 500 }}>{s.usage.toLocaleString()}</td>
                            <td style={{ padding: '6px 10px', fontSize: 12.5, color: 'var(--text-secondary)' }}>{s.pool_at_sample.toLocaleString()}</td>
                            <td style={{ padding: '6px 10px', fontSize: 12.5, fontWeight: s.overage > 0 ? 700 : 400, color: s.overage > 0 ? '#D97706' : 'var(--text-muted)' }}>
                              {s.overage > 0 ? `+${s.overage.toLocaleString()}` : '0'}
                            </td>
                            <td style={{ padding: '6px 10px', fontSize: 11.5, color: 'var(--text-muted)' }}>{s.source}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}
